<template>
  <div
    v-if="value"
    class="ujdf-overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="ujdf-title"
    data-testid="upload-job-description-file"
    @keydown.esc="handleDiscard"
  >
    <div class="ujdf-overlay__backdrop" @click="handleDiscard" />

    <div class="ujdf-dialog" :class="{ 'ujdf-dialog--uploading': isUploading }">
      <img
        class="ujdf-dialog__pattern"
        :src="patternSrc"
        alt=""
        width="336"
        height="336"
      />

      <div class="ujdf-header">
        <div class="ujdf-featured-icon" aria-hidden="true">
          <img :src="uploadCloudSrc" alt="" width="24" height="24" />
        </div>
        <h2 id="ujdf-title" class="ujdf-title">Upload Job Description</h2>
        <button
          type="button"
          class="ujdf-close"
          aria-label="Close"
          data-testid="ujdf-close"
          :disabled="isUploading"
          @click="handleDiscard"
        >
          <Icon name="popup_close" />
        </button>
      </div>

      <div class="ujdf-body">
        <button
          v-if="!selectedFile"
          type="button"
          class="ujdf-dropzone"
          :class="{ 'ujdf-dropzone--dragover': isDragging }"
          data-testid="ujdf-dropzone"
          @click="openFilePicker"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleDrop"
        >
          <span class="ujdf-dropzone__icon" aria-hidden="true">
            <img :src="uploadCloudSrc" alt="" width="20" height="20" />
          </span>
          <p class="ujdf-dropzone__action">
            <span class="ujdf-dropzone__link">Click to upload</span>
            <span class="ujdf-dropzone__or">or drag and drop</span>
          </p>
          <p class="ujdf-dropzone__hint">DOC, DOCX, PDF</p>
        </button>

        <div v-else class="ujdf-file" data-testid="ujdf-file">
          <JobDescriptionFileTypeIcon
            :file-name="selectedFile.name"
            :file-type="selectedFile.type"
            :size="40"
          />
          <div class="ujdf-file__meta">
            <p class="ujdf-file__name" :title="selectedFile.name">{{ selectedFile.name }}</p>
            <p class="ujdf-file__size">{{ formattedSize }}</p>
            <div v-if="isUploading" class="ujdf-progress" data-testid="ujdf-progress">
              <div class="ujdf-progress__track">
                <div class="ujdf-progress__fill" :style="{ width: `${uploadProgress}%` }" />
              </div>
            </div>
          </div>
          <button
            type="button"
            class="ujdf-file__remove"
            aria-label="Remove file"
            data-testid="ujdf-remove-file"
            @click="clearFile"
          >
            <img :src="trashSrc" alt="" width="20" height="20" />
          </button>
        </div>

        <p v-if="uploadError" class="ujdf-error" role="alert" data-testid="ujdf-error">
          {{ uploadError }}
        </p>
      </div>

      <div v-if="!isUploading" class="ujdf-actions">
        <StandardButton
          type="magenta"
          variant="outline"
          size="normal"
          label="Discard"
          class="ujdf-actions__btn"
          data-testid="ujdf-discard"
          @click="handleDiscard"
        />
        <StandardButton
          type="magenta"
          variant="primary"
          size="normal"
          label="Upload"
          class="ujdf-actions__btn"
          data-testid="ujdf-upload"
          @click="handleUploadClick"
        />
      </div>
    </div>

    <input
      ref="fileInput"
      type="file"
      class="ujdf-input"
      accept=".doc,.docx,.pdf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      tabindex="-1"
      aria-label="Choose job description file"
      data-testid="ujdf-file-input"
      @change="handleFileInput"
    />
  </div>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import { startJobDescriptionImport } from "@/api/positionAPI";
import JobDescriptionFileTypeIcon from "./JobDescriptionFileTypeIcon.vue";

const ACCEPTED_EXT = ["pdf", "doc", "docx"];
const DEMO_UPLOAD_MS = 1200;
const IMPORT_ALREADY_RUNNING = "IMPORT_ALREADY_RUNNING";
const IMPORT_BLOCKED_MESSAGE =
  "This position already has an import in progress. Finish or discard the current import before uploading another job description.";
const UPLOAD_FAILED_MESSAGE = "We could not upload this file. Please try again.";

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  /** Background import (idea-MDP-644): create the import on the server instead of the demo timer. */
  backgroundImport: {
    type: Boolean,
    default: false,
  },
  positionId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(["input", "discard", "uploaded"]);

const fileInput = ref(null);
const selectedFile = ref(null);
const isDragging = ref(false);
const isUploading = ref(false);
const uploadProgress = ref(0);
const uploadError = ref("");
let progressTimer = null;

const patternSrc = require("@components/assets/Attach_Background_pattern_decorative.svg");
const uploadCloudSrc = require("@components/assets/upload-cloud-02.svg");
const trashSrc = require("@components/assets/trash-01.svg");

const formattedSize = computed(() => formatFileSize(selectedFile.value?.size));

watch(
  () => props.value,
  (open) => {
    if (!open) resetState();
  }
);

onUnmounted(() => {
  stopProgress();
  removeUnloadWarning();
});

// Warn on tab close only while the file is still uploading; once the import exists the server owns it.
function warnOnUnload(event) {
  event.preventDefault();
  event.returnValue = "";
  return "";
}

function addUnloadWarning() {
  window.addEventListener("beforeunload", warnOnUnload);
}

function removeUnloadWarning() {
  window.removeEventListener("beforeunload", warnOnUnload);
}

function extensionOf(file) {
  const nameExt = String(file?.name || "")
    .toLowerCase()
    .split(".")
    .pop();
  if (ACCEPTED_EXT.includes(nameExt)) return nameExt;
  const type = String(file?.type || "").toLowerCase();
  if (type.includes("pdf")) return "pdf";
  if (type.includes("wordprocessingml") || type.includes("docx")) return "docx";
  if (type.includes("msword") || type.includes("doc")) return "doc";
  return "";
}

function isAccepted(file) {
  return ACCEPTED_EXT.includes(extensionOf(file));
}

function formatFileSize(bytes) {
  const value = Number(bytes) || 0;
  if (value >= 1024 * 1024) {
    const mb = value / (1024 * 1024);
    return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`;
  }
  if (value >= 1024) {
    return `${Math.round(value / 1024)} KB`;
  }
  return `${value} B`;
}

function openFilePicker() {
  if (isUploading.value) return;
  fileInput.value?.click();
}

function assignFile(file) {
  if (!file || !isAccepted(file)) return;
  selectedFile.value = file;
  uploadError.value = "";
  isDragging.value = false;
}

function handleFileInput(event) {
  const file = event?.target?.files?.[0];
  event.target.value = "";
  assignFile(file);
}

function handleDrop(event) {
  isDragging.value = false;
  assignFile(event?.dataTransfer?.files?.[0]);
}

function clearFile() {
  stopProgress();
  isUploading.value = false;
  uploadError.value = "";
  uploadProgress.value = 0;
  selectedFile.value = null;
}

function handleDiscard() {
  if (isUploading.value) return;
  resetState();
  emit("input", false);
  emit("discard");
}

function handleUploadClick() {
  if (!selectedFile.value) {
    openFilePicker();
    return;
  }
  startUpload();
}

async function startBackgroundUpload() {
  const file = selectedFile.value;
  isUploading.value = true;
  uploadProgress.value = 0;
  uploadError.value = "";
  addUnloadWarning();
  try {
    const response = await startJobDescriptionImport({
      positionId: props.positionId,
      file,
      onUploadProgress: (event) => {
        if (event?.total) {
          uploadProgress.value = Math.min(100, Math.round((event.loaded / event.total) * 100));
        }
      },
    });
    uploadProgress.value = 100;
    removeUnloadWarning();
    emit("uploaded", {
      name: file.name,
      type: file.type || extensionOf(file),
      size: file.size,
      file,
      importId: response?.data?.id ?? null,
    });
  } catch (error) {
    removeUnloadWarning();
    isUploading.value = false;
    uploadProgress.value = 0;
    const blocked =
      error?.response?.status === 409 && error?.response?.data?.code === IMPORT_ALREADY_RUNNING;
    uploadError.value = blocked ? IMPORT_BLOCKED_MESSAGE : UPLOAD_FAILED_MESSAGE;
  }
}

function startUpload() {
  if (!selectedFile.value || isUploading.value) return;
  if (props.backgroundImport) {
    startBackgroundUpload();
    return;
  }
  isUploading.value = true;
  uploadProgress.value = 0;
  const started = Date.now();
  progressTimer = setInterval(() => {
    const elapsed = Date.now() - started;
    const next = Math.min(100, Math.round((elapsed / DEMO_UPLOAD_MS) * 100));
    uploadProgress.value = next;
    if (next >= 100) {
      finishUpload();
    }
  }, 40);
}

function finishUpload() {
  stopProgress();
  uploadProgress.value = 100;
  const file = selectedFile.value;
  emit("uploaded", {
    name: file.name,
    type: file.type || extensionOf(file),
    size: file.size,
    file,
  });
}

function stopProgress() {
  if (progressTimer) {
    clearInterval(progressTimer);
    progressTimer = null;
  }
}

function resetState() {
  stopProgress();
  removeUnloadWarning();
  uploadError.value = "";
  selectedFile.value = null;
  isDragging.value = false;
  isUploading.value = false;
  uploadProgress.value = 0;
}

defineExpose({
  assignFile,
  fileInput,
});
</script>

<style scoped>
.ujdf-overlay {
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.ujdf-overlay__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(16, 24, 40, 0.4);
}

.ujdf-dialog {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 546px;
  max-width: 100%;
  overflow: hidden;
  background: #ffffff;
  border-radius: 12px;
  box-shadow:
    0 20px 24px -4px rgba(16, 24, 40, 0.08),
    0 8px 8px -4px rgba(16, 24, 40, 0.03);
}

.ujdf-dialog--uploading {
  padding-bottom: 24px;
}

.ujdf-dialog__pattern {
  position: absolute;
  top: -120px;
  left: -120px;
  width: 336px;
  height: 336px;
  opacity: 0.5;
  pointer-events: none;
}

.ujdf-header {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding: 24px 24px 20px;
}

.ujdf-featured-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.ujdf-title {
  margin: 0;
  width: 100%;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
  text-align: center;
}

.ujdf-close {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 8px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.ujdf-close:hover {
  background: #f9fafb;
}

.ujdf-close:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.ujdf-body {
  padding: 0 24px;
}

.ujdf-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 16px 24px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  cursor: pointer;
}

.ujdf-dropzone--dragover {
  border-color: #7f56d9;
  background: #f9f5ff;
}

.ujdf-dropzone__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-bottom: 8px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.ujdf-dropzone__action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin: 0;
}

.ujdf-dropzone__link {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #6941c6;
}

.ujdf-dropzone__or {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.ujdf-dropzone__hint {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #475467;
  text-align: center;
}

.ujdf-file {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
}

.ujdf-file__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
  padding-right: 28px;
}

.ujdf-file__name,
.ujdf-file__size {
  margin: 0;
  overflow: hidden;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ujdf-file__name {
  font-weight: 500;
  color: #344054;
}

.ujdf-file__size {
  font-weight: 400;
  color: #475467;
}

.ujdf-file__remove {
  position: absolute;
  top: 7px;
  right: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 8px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.ujdf-file__remove:hover {
  background: #f9fafb;
}

.ujdf-progress {
  width: 100%;
  margin-top: 4px;
}

.ujdf-progress__track {
  position: relative;
  flex: 1;
  height: 8px;
  overflow: hidden;
  background: #eaecf0;
  border-radius: 9999px;
}

.ujdf-progress__fill {
  height: 100%;
  background: #7f56d9;
  border-radius: 9999px;
  transition: width 80ms linear;
}

.ujdf-error {
  margin: 12px 0 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #b42318;
}

.ujdf-actions {
  display: flex;
  gap: 12px;
  padding: 32px 24px 24px;
}

.ujdf-actions :deep(.standard-button) {
  flex: 1 1 0;
  min-width: 0;
  width: auto;
}

.ujdf-input {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
